import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDownIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/16/solid';
import { useEffect, useState } from 'react';

import { dispatchShowAlert } from '@/renderer/utils/alerts';
import { request } from '@/renderer/utils/api';
import copyToClipboard from '@/renderer/utils/copyToClipboard';
import useMe from '@/renderer/utils/useMe';
import type { RequestSchema } from '@/types/request';

// Heroicons ships no Bitcoin glyph, so this is a small inline ₿ mark — the standard
// Bitcoin badge (a filled disc with the cut-out B) drawn with `currentColor` so it
// inherits the toolbar's `text-zinc-*` like the sibling heroicons. Sized via
// className exactly like a heroicon (e.g. `size-3.5`); solid to match the 16/solid
// icons around it. Distinct at icon-only mobile size from the pending-plays ticket.
function BitcoinIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      role="img"
    >
      <path d="M23.638 14.904c-1.602 6.43-8.113 10.34-14.542 8.736C2.67 22.05-1.244 15.525.362 9.105 1.962 2.67 8.475-1.243 14.9.358c6.43 1.605 10.342 8.115 8.738 14.548v-.002zm-6.35-4.613c.24-1.59-.974-2.45-2.64-3.03l.54-2.153-1.315-.325-.525 2.107c-.345-.087-.705-.167-1.064-.25l.526-2.127-1.32-.33-.54 2.165c-.285-.067-.565-.132-.84-.2l-1.815-.45-.35 1.407s.975.225.955.236c.535.136.63.486.615.766l-1.477 5.92c-.075.166-.24.406-.614.314.015.02-.96-.24-.96-.24l-.66 1.51 1.71.426.93.242-.54 2.19 1.32.327.54-2.17c.36.1.705.19 1.05.273l-.51 2.154 1.32.33.545-2.19c2.24.427 3.93.257 4.64-1.774.57-1.637-.03-2.58-1.217-3.196.854-.193 1.5-.76 1.68-1.93h.01zm-3.01 4.22c-.404 1.64-3.157.75-4.05.53l.72-2.9c.896.23 3.757.67 3.33 2.37zm.41-4.24c-.37 1.49-2.662.735-3.405.55l.654-2.64c.744.18 3.137.524 2.75 2.084v.006z" />
    </svg>
  );
}

// Admin/staff-only convenience (#1803): copy pre-formatted crypto payment
// instructions to the clipboard so support can paste them straight into a
// support-chat reply when a buyer wants to pay by BTC or ETH. Sibling of the
// "Copy pending plays" split button — same clipboard/toast pattern, same split
// chrome, so hover/caret behavior stays consistent.
//
// The receiving addresses live in server env (ConfigMap `gkeraspicks-work-config`,
// never git) and are fetched at click-time from the admin endpoint below, so they
// rotate by editing the ConfigMap without a client deploy. They are PUBLIC
// receiving addresses (safe to share with customers); keeping them server-side
// only means they aren't baked into the built bundle.
//
// The copied blob is single-network and unambiguous (labels the network, no amount
// baked in — that varies per order). The main button copies the BTC blob (the most
// common crypto request); the caret opens a menu to pick BTC or ETH explicitly.
const ADDRESSES_ENDPOINT = '/api/v1.1/admin/crypto-payment-addresses';

type CryptoNetwork = 'btc' | 'eth';

interface CryptoAddresses {
  btc: string;
  eth: string;
}

const NETWORKS: { key: CryptoNetwork; label: string }[] = [
  { key: 'btc', label: 'Bitcoin (BTC)' },
  { key: 'eth', label: 'Ethereum (ETH)' },
];

/** Human label for a network, used in tooltips, the menu, and toasts. */
function labelOf(network: CryptoNetwork): string {
  return NETWORKS.find((n) => n.key === network)?.label ?? network.toUpperCase();
}

/**
 * Build the pre-formatted, single-network payment instructions for the given
 * network + address. No amount is baked in (it varies per order); the note asks
 * the buyer to send the exact order amount and reply with the transaction so
 * support can confirm it. Kept one-network-per-blob so BTC and ETH never mix.
 */
export function buildCryptoInstructions(network: CryptoNetwork, address: string): string {
  if (network === 'eth') {
    return [
      'Pay with Ethereum (ETH)',
      '',
      'Network: Ethereum (ERC-20)',
      `Address: ${address}`,
      '',
      'Send the exact amount for your order in ETH to the address above, then reply here with the transaction hash so we can confirm it. Only send ETH on the Ethereum (ERC-20) network to this address.',
    ].join('\n');
  }

  return [
    'Pay with Bitcoin (BTC)',
    '',
    'Network: Bitcoin (BTC)',
    `Address: ${address}`,
    '',
    'Send the exact amount for your order in BTC to the address above, then reply here with the transaction ID (txid) so we can confirm it. Only send BTC on the Bitcoin network to this address.',
  ].join('\n');
}

/** Fetch the configured BTC/ETH receiving addresses. Throws on request failure. */
async function loadAddresses(): Promise<CryptoAddresses> {
  const res = (await request(
    ADDRESSES_ENDPOINT as unknown as RequestSchema<{ data: CryptoAddresses }>,
  )) as { data?: Partial<CryptoAddresses> } | undefined;
  return { btc: res?.data?.btc ?? '', eth: res?.data?.eth ?? '' };
}

/**
 * Drop one network's instructions (address already resolved) into the reply and
 * toast the outcome. When an `onInsert` is supplied (the composer is mounted), the
 * text goes straight into the draft — no clipboard round trip. Falls back to a
 * clipboard copy if the composer can't take the text (or no callback was given).
 */
async function copyNetwork(
  network: CryptoNetwork,
  address: string,
  onInsert?: (text: string) => boolean,
): Promise<void> {
  const label = labelOf(network);
  if (!address) {
    dispatchShowAlert({ message: `No ${label} address is configured`, status: 'info' });
    return;
  }

  const text = buildCryptoInstructions(network, address);

  if (onInsert?.(text)) {
    dispatchShowAlert({ message: `Inserted ${label} payment instructions`, status: 'success' });
    return;
  }

  const ok = await copyToClipboard(text);
  dispatchShowAlert(
    ok
      ? { message: `Copied ${label} payment instructions`, status: 'success' }
      : { message: 'Could not copy to clipboard', status: 'error' },
  );
}

/**
 * The per-network menu, rendered inside the popover panel. Fetches on mount (i.e.
 * when the popover opens) and lists one row per network. Picking a row copies just
 * that network's blob and closes the popover; a network with no configured address
 * is shown disabled so it's clear which are set.
 */
function NetworkMenu({
  onPicked,
  onInsert,
}: {
  onPicked: () => void;
  onInsert?: (text: string) => boolean;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addresses, setAddresses] = useState<CryptoAddresses>({ btc: '', eth: '' });

  useEffect(() => {
    let alive = true;
    loadAddresses()
      .then((addr) => {
        if (!alive) {
          return;
        }
        setAddresses(addr);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (!alive) {
          return;
        }
        setError(err?.message || 'Could not load crypto addresses');
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const pick = async (network: CryptoNetwork) => {
    onPicked();
    await copyNetwork(network, addresses[network], onInsert);
  };

  if (loading) {
    return <div className="px-3 py-2 text-xs text-zinc-500 dark:text-zinc-400">Loading…</div>;
  }
  if (error) {
    return <div className="px-3 py-2 text-xs text-rose-600 dark:text-rose-400">{error}</div>;
  }

  const rowClass =
    'flex w-full items-center justify-between gap-4 px-3 py-1.5 text-left text-xs text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-zinc-200 dark:hover:bg-zinc-800';

  return (
    <div className="py-1">
      {NETWORKS.map((n) => {
        const configured = !!addresses[n.key];
        return (
          <button
            type="button"
            key={n.key}
            className={rowClass}
            disabled={!configured}
            onClick={() => pick(n.key)}
          >
            <span>{n.label}</span>
            {!configured && <span className="text-zinc-400 dark:text-zinc-500">not set</span>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * "Copy crypto payment" split button for the support-chat composer. Admin/staff only —
 * renders nothing for members. The main button copies the BTC instructions; the caret
 * opens a menu to copy BTC or ETH explicitly.
 */
export default function CopyCryptoPaymentButton({
  onInsert,
}: {
  // Insert the snippet straight into the composer draft; returns false if that
  // isn't possible so we fall back to a clipboard copy (#1817 follow-up).
  onInsert?: (text: string) => boolean;
} = {}) {
  const me = useMe();
  const isStaffViewer = me?.role === 'admin' || me?.role === 'staff';
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // Label reflects the primary action: insert into the composer when wired up,
  // otherwise the legacy clipboard copy.
  const doneLabel = onInsert ? 'Inserted' : 'Copied';

  // Gate on role — a member never sees this, and it self-gates even if dropped
  // somewhere its parent forgot to check the role.
  if (!isStaffViewer) {
    return null;
  }

  const copyBtc = async () => {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      const { btc } = await loadAddresses();
      await copyNetwork('btc', btc, onInsert);
      if (btc) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      dispatchShowAlert({
        message: (err as Error)?.message || 'Could not load crypto addresses',
        status: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  // One split control: a main button + a caret column, drawn as a single rounded,
  // ring-outlined unit that matches the composer's other controls. Mirrors
  // CopyPendingPlaysButton exactly — the outline lives in a `pointer-events-none`
  // overlay ABOVE the buttons so a child's hover background can't nibble the border,
  // and the caret's `border-l` is interior so it stays crisp under either hover.
  return (
    <div className="relative inline-flex items-stretch rounded-md text-xs font-medium text-zinc-600 dark:text-zinc-300">
      <button
        type="button"
        onClick={copyBtc}
        disabled={busy}
        title={
          onInsert
            ? 'Insert Bitcoin (BTC) payment instructions into your reply'
            : 'Copy Bitcoin (BTC) payment instructions to your clipboard'
        }
        aria-label={
          onInsert
            ? 'Insert Bitcoin (BTC) payment instructions into your reply'
            : 'Copy Bitcoin (BTC) payment instructions to your clipboard'
        }
        className="inline-flex items-center gap-1.5 rounded-l-md px-2 py-1 hover:bg-zinc-100 disabled:opacity-50 dark:hover:bg-zinc-800"
      >
        {copied ? (
          <ClipboardDocumentCheckIcon className="size-3.5 shrink-0 text-emerald-500" />
        ) : (
          <BitcoinIcon className="size-3.5 shrink-0" />
        )}
        {/*
          Icon-only on small screens to save composer real estate (the tooltip /
          aria-label carry the meaning); the label shows from sm up. `whitespace-nowrap`
          guarantees it never breaks into stacked lines — a narrow parent wraps the whole
          control to its own line instead.
        */}
        <span className="hidden whitespace-nowrap sm:inline">
          {copied ? doneLabel : busy ? 'Working…' : 'BTC pay'}
        </span>
      </button>
      <Popover as="div" className="relative flex">
        <PopoverButton
          title="Copy BTC or ETH payment instructions"
          aria-label="Copy BTC or ETH payment instructions"
          className="flex w-7 items-center justify-center self-stretch rounded-r-md border-zinc-950/10 border-l hover:bg-zinc-100 focus:outline-none dark:border-white/10 dark:hover:bg-zinc-800"
        >
          <ChevronDownIcon className="size-3.5 shrink-0" />
        </PopoverButton>
        <PopoverPanel
          transition
          // `anchor` portals the panel to the document root and positions it with
          // collision handling (flips/shifts to stay on screen), so it no longer gets
          // clipped by the composer's overflow when the toolbar is near a window edge.
          // Opens above the caret ("top end"); Floating UI flips it below if there's no
          // room. Replaces the old absolute `bottom-full` positioning.
          anchor={{ to: 'top end', gap: 8, padding: 8 }}
          className="z-20 max-h-72 w-44 origin-bottom-right overflow-auto rounded-md bg-white shadow-lg ring-1 ring-zinc-950/10 transition data-closed:scale-95 data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in dark:bg-zinc-900 dark:ring-white/10"
        >
          {({ close }) => <NetworkMenu onPicked={close} onInsert={onInsert} />}
        </PopoverPanel>
      </Popover>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-md ring-1 ring-zinc-950/10 ring-inset dark:ring-white/10"
      />
    </div>
  );
}
